import { ActionType } from '@evidentid/tprm-portal-lib/models/entity-actions-review';

// $action literals shared by AcceptanceActionResolveInput: their result carries only a
// boolean accept/reject decision, described identically regardless of action type.
const ACCEPTANCE_ACTION_TYPES: ActionType[] = [
    ActionType.entityNameMatchingV1,
    ActionType.complianceWithCategoryResolution,
    ActionType.evaluatedEntityMappingV1,
    ActionType.aieManuscriptCategorization,
];

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function describeNonExtraction(payload: Record<string, unknown>): string[] {
    const { requirementResults, recollectionDate } = payload;
    if (!isPlainObject(requirementResults)) {
        return [];
    }
    const outcomes = Object.values(requirementResults);
    const met = outcomes.filter(Boolean).length;
    const lines = [ `${met} of ${outcomes.length} requirements met` ];
    if (typeof recollectionDate === 'string' && recollectionDate) {
        lines.push(`Recollection date: ${recollectionDate}`);
    }
    return lines;
}

/** Turns a resolved action's stored result into the lines `ResolutionSummary` renders under
 * its decision.
 *
 * The result is the same payload the resolve request sent, so it is only ever shaped like the
 * resolve-input union - never a fresh object built for display. It may also predate a field the
 * current type declares, since actions resolved before this summary existed (or before a field
 * was added) carry whatever an older build wrote, including nothing at all. Every field is read
 * defensively for that reason, and any result that isn't a describable object - most commonly a
 * missing one on rows resolved before revert existed - yields no lines rather than an error.
 */
export function getResolutionResultSummary(actionType: ActionType, result: unknown): string[] {
    if (!isPlainObject(result)) {
        return [];
    }

    if (ACCEPTANCE_ACTION_TYPES.includes(actionType)) {
        return typeof result.accepted === 'boolean'
            ? [ result.accepted ? 'Accepted' : 'Rejected' ]
            : [];
    }

    switch (actionType) {
        case ActionType.endorsementFormNumberV1:
            return [
                typeof result.category === 'string' && result.category
                    ? `Categorized as "${result.category}"`
                    : 'No category assigned',
            ];
        case ActionType.nonExtractionCriterionResolutionV1:
            return describeNonExtraction(result);
        default:
            return [];
    }
}
